// Monetisation → Ads (contracts/ads.md §5): the master switch, provider and test mode; the provider's App ID
// and unit ids (validated per provider); format toggles and the placements editor; grace days, the country
// filter and, for AdMob, the SSV toggle with the callback URL; then the rewarded-ad stats. Every change is a
// config key (PUT /config/:key, audited) behind a confirm step that lists what changes. Editors may change
// formats and placements; everything else needs an admin; viewers read.

import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useAdsStats } from "../api/ads";
import type { AdsStats } from "../api/adsSchemas";
import { useConfig, useSetConfig } from "../api/queries";
import type { JsonValue } from "../api/schemas";
import { useAuth, useCan } from "../auth/AuthContext";
import { BarChart, ColumnChart, type BarDatum } from "../components/charts/charts";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, Stat, TextInput } from "../components/ui";
import {
  AD_UNIT_KINDS,
  ADMANAGER_TEST_IDS,
  ADMOB_TEST_IDS,
  adsSettings,
  appIdProblem,
  COUNTRY,
  FIELD_LABEL,
  FORMAT_LABEL,
  PLACEMENTS,
  PROVIDER_LABEL,
  REWARDED_LABEL,
  REWARDED_PLACEMENTS,
  SSV_CALLBACK_URL,
  UNIT_LABEL,
  unitIdProblem,
  networkCodeProblem,
  WEB_AD_UNITS,
  WEB_UNIT_LABEL,
  webUnitProblem,
  type AdsWeb,
  type WebAdUnit,
  type WebCmp,
  type AdCountries,
  type AdFormats,
  type AdIds,
  type AdPlacements,
  type AdProvider,
  type AdsSettings,
  type IdProvider,
  type PlacementField,
  type RewardedPlacement,
} from "../lib/ads";
import { addDays, formatShortDate, todayUtc } from "../lib/dates";
import { fmtInt, fmtPct, shortId } from "../lib/format";
import a from "./ads.module.css";
import pages from "./pages.module.css";

// ---------------------------------------------------------------- change lists (the confirm step)

type Change = {
  path: string;
  from: JsonValue | undefined;
  to: JsonValue | undefined;
};

/** Leaf-level differences between two JSON values (`rewardedHint.dailyCap: 5 → 4`). */
export function changesBetween(from: JsonValue | undefined, to: JsonValue | undefined, path = ""): Change[] {
  const isObj = (v: unknown): v is Record<string, JsonValue> => typeof v === "object" && v !== null && !Array.isArray(v);
  if (isObj(from) && isObj(to)) {
    const keys = [...new Set([...Object.keys(from), ...Object.keys(to)])];
    return keys.flatMap((k) => changesBetween(from[k], to[k], path ? `${path}.${k}` : k));
  }
  return JSON.stringify(from) === JSON.stringify(to) ? [] : [{ path, from, to }];
}

const show = (v: JsonValue | undefined) => (v === undefined ? "(unset)" : JSON.stringify(v));
const same = (x: unknown, y: unknown) => JSON.stringify(x) === JSON.stringify(y);

interface Pending {
  key: string;
  title: string;
  from: JsonValue | undefined;
  to: JsonValue;
  note?: ReactNode;
}

/** A draft that follows the server value until it's edited (and again after a save). */
function useDraft<T>(server: T) {
  const [draft, setDraft] = useState(server);
  const [base, setBase] = useState(JSON.stringify(server));
  if (base !== JSON.stringify(server)) {
    setBase(JSON.stringify(server));
    setDraft(server);
  }
  return [draft, setDraft, !same(draft, server)] as const;
}

// ---------------------------------------------------------------- page

export function AdsPage() {
  const q = useConfig();
  const canEdit = useCan("ads.edit");
  const canConfigure = useCan("ads.configure");
  const { admin } = useAuth();
  const mutation = useSetConfig();
  const [pending, setPending] = useState<Pending | null>(null);

  const header = (
    <PageHeader
      title="Ads"
      meta={
        <span>
          Free players only: Pro and Lifetime never see ads or reward offers. Rewarded first; interstitials only between levels; nothing in the daily,
          Hard Mode, competitive modes, onboarding, the paywall or sharing. Every change is a config key, audited, and reaches the app through{" "}
          <code>GET /v1/config</code> without an update.
        </span>
      }
      actions={
        canConfigure ? (
          <span className="monoLabel">admin · every setting</span>
        ) : (
          <span className={pages.readOnly} data-testid="ads-role-note">
            {canEdit ? "Editor · formats and placements only" : `Read-only · you are ${admin?.role}`}
          </span>
        )
      }
    />
  );

  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );

  const values = q.data.values;
  const s = adsSettings(values);
  const request = (key: string, to: JsonValue, title: string, note?: ReactNode) => {
    mutation.reset();
    setPending({ key, title, from: values[key], to, note });
  };
  const changes = pending ? changesBetween(pending.from, pending.to) : [];

  return (
    <>
      {header}
      {s.testMode ? (
        <div className={a.testBanner} role="status" data-testid="test-banner">
          <span className={a.testDot} aria-hidden />
          <strong>Test ads only</strong>
          <span>
            Every unit is Google&rsquo;s test unit for {PROVIDER_LABEL[s.provider]}, whatever the id fields say. Turn test mode off once the real ids
            are in and the build carries the real App ID.
          </span>
        </div>
      ) : null}
      <div className={a.grid}>
        <div className={a.column}>
          <SwitchesCard s={s} canConfigure={canConfigure} request={request} />
          <RulesCard s={s} canConfigure={canConfigure} request={request} />
        </div>
        <div className={a.column}>
          <IdsCard s={s} canConfigure={canConfigure} request={request} />
          <FormatsCard s={s} canEdit={canEdit} request={request} />
        </div>
        <PlacementsCard s={s} canEdit={canEdit} request={request} />
        <WebCard s={s} canConfigure={canConfigure} request={request} />
      </div>
      <StatsSection />

      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ""}
        confirmLabel="Save"
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setPending(null)}
        onConfirm={() => pending && mutation.mutate({ key: pending.key, value: pending.to }, { onSuccess: () => setPending(null) })}
      >
        {pending ? (
          <div className={pages.diff} data-testid="ads-diff">
            <code>{pending.key}</code>
            {changes.map((c) => (
              <div key={c.path || "value"} className={a.change}>
                {c.path ? <code className={a.path}>{c.path}</code> : null}
                <div className={pages.diffRow} data-kind="from">
                  <span className="monoLabel">before</span>
                  <code>{show(c.from)}</code>
                </div>
                <div className={pages.diffRow} data-kind="to">
                  <span className="monoLabel">after</span>
                  <code>{show(c.to)}</code>
                </div>
              </div>
            ))}
            {pending.note ? <p className={pages.faint}>{pending.note}</p> : null}
            <p className={pages.faint}>Written to the audit log. Apps pick it up on their next config fetch.</p>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

type RequestFn = (key: string, to: JsonValue, title: string, note?: ReactNode) => void;

function ReadOnly({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <span className={pages.readOnly} data-testid={testId}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- switches

function Switch({
  checked,
  label,
  disabled,
  onClick,
  testId,
}: {
  checked: boolean;
  label: string;
  disabled: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      className={a.switch}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      <span className={a.switchKnob} />
    </button>
  );
}

function SwitchesCard({ s, canConfigure, request }: { s: AdsSettings; canConfigure: boolean; request: RequestFn }) {
  const providers: {
    value: AdProvider;
    label: string;
    disabled?: boolean;
    title?: string;
  }[] = [
    { value: "admanager", label: PROVIDER_LABEL.admanager },
    { value: "admob", label: PROVIDER_LABEL.admob },
    {
      value: "unity",
      label: "Unity · coming later",
      disabled: true,
      title: "Reserved: the apps don't ship a Unity SDK yet",
    },
  ];
  return (
    <Card title="Main switches" actions={<Chip tone={s.enabled ? "accent" : "muted"}>{s.enabled ? "ads on" : "ads off"}</Chip>}>
      <div className={a.switchRow}>
        <Switch
          checked={s.enabled}
          label="Ads for free players (ads.enabled)"
          disabled={!canConfigure}
          testId="ads-master"
          onClick={() =>
            request(
              "ads.enabled",
              !s.enabled,
              s.enabled ? "Turn ads off for everyone?" : "Turn ads on for free players?",
              s.enabled
                ? "Apps stop loading ads and reward offers disappear; rewards already earned stay."
                : s.testMode
                  ? "Test mode is on: players see Google's test ads."
                  : "Test mode is off: real ads, real revenue.",
            )
          }
        />
        <div className={a.switchText}>
          <strong>{s.enabled ? "Ads are on for free players" : "Ads are off"}</strong>
          <p>The master kill switch. Off: the app loads nothing and reward intents answer 403 ads_off.</p>
        </div>
      </div>
      <div className={a.row}>
        <span className="monoLabel">Provider</span>
        <Segmented
          label="Ad provider"
          value={s.provider}
          options={providers}
          disabled={!canConfigure}
          onChange={(v) =>
            v !== s.provider &&
            request(
              "ads.provider",
              v,
              `Switch ads to ${PROVIDER_LABEL[v]}?`,
              v === "admob"
                ? "AdMob rewards can wait for Google's SSV callback (Rules)."
                : "Ad Manager has no SSV: rewards are claimed by the app, with server-side caps and checks.",
            )
          }
        />
      </div>
      <div className={a.switchRow}>
        <Switch
          checked={s.testMode}
          label="Test mode (ads.testMode)"
          disabled={!canConfigure}
          testId="ads-test-mode"
          onClick={() =>
            request(
              "ads.testMode",
              !s.testMode,
              s.testMode ? "Serve real ads?" : "Back to test ads?",
              s.testMode
                ? "The apps load the unit ids below. Make sure they are the real ones."
                : "The apps load Google's test units whatever the fields say.",
            )
          }
        />
        <div className={a.switchText}>
          <strong>{s.testMode ? "Test mode: Google's test units" : "Real units"}</strong>
          <p>In test mode the server hands out the provider&rsquo;s test ids; test units never call SSV.</p>
        </div>
      </div>
      {!canConfigure ? <ReadOnly testId="switches-read-only">Read-only · the switches need the admin role</ReadOnly> : null}
    </Card>
  );
}

// ---------------------------------------------------------------- ids

function IdsCard({ s, canConfigure, request }: { s: AdsSettings; canConfigure: boolean; request: RequestFn }) {
  const active: IdProvider = s.provider === "admob" ? "admob" : "admanager";
  const [view, setView] = useState<IdProvider>(active);
  // When the provider changes, show the ids now in use.
  const [shownFor, setShownFor] = useState<IdProvider>(active);
  if (shownFor !== active) {
    setShownFor(active);
    setView(active);
  }
  const server = view === "admob" ? s.admob : s.admanager;
  // Switching tabs changes `server`, so the draft follows that provider's stored ids.
  const [draft, setDraft, dirty] = useDraft<AdIds>(server);
  const appProblem = appIdProblem(view, draft.appId);
  const unitProblems = Object.fromEntries(AD_UNIT_KINDS.map((k) => [k, unitIdProblem(view, draft.units[k])])) as Record<
    (typeof AD_UNIT_KINDS)[number],
    string | null
  >;
  const valid = !appProblem && Object.values(unitProblems).every((p) => p === null);
  const testIds = view === "admob" ? ADMOB_TEST_IDS : ADMANAGER_TEST_IDS;
  const key = view === "admob" ? "ads.admob" : "ads.admanager";
  const set = (patch: Partial<AdIds> & { unit?: [string, string] }) =>
    setDraft({
      appId: patch.appId ?? draft.appId,
      units: patch.unit ? { ...draft.units, [patch.unit[0]]: patch.unit[1] } : draft.units,
    });

  return (
    <Card
      title={view === "admob" ? "AdMob IDs" : "Ad Manager IDs"}
      actions={
        <>
          {view === active ? <Chip tone="accent">in use</Chip> : <Chip tone="muted">not in use</Chip>}
          {view === active && s.testMode ? <Chip tone="warning">test units served</Chip> : null}
        </>
      }
    >
      <div className={a.row}>
        <span className="monoLabel">IDs for</span>
        <Segmented
          label="Provider ids"
          value={view}
          options={[
            { value: "admanager", label: PROVIDER_LABEL.admanager },
            { value: "admob", label: PROVIDER_LABEL.admob },
          ]}
          onChange={setView}
        />
      </div>
      <div className={a.fields} key={view}>
        <Field
          label={view === "admanager" ? "App ID (optional)" : "App ID"}
          hint={
            view === "admanager"
              ? "Built into the app: Ad Manager apps can run without one (GADIsAdManagerApp)."
              : "Built into the app (GADApplicationIdentifier); changing it needs a new build."
          }
        >
          <TextInput
            value={draft.appId}
            placeholder={view === "admanager" ? "none" : "ca-app-pub-…~…"}
            onChange={(e) => set({ appId: e.target.value })}
            readOnly={!canConfigure}
            aria-invalid={appProblem ? true : undefined}
            spellCheck={false}
            className={a.mono}
            data-testid="app-id"
          />
          {appProblem ? <span className={pages.fieldError}>{appProblem}</span> : null}
        </Field>
        {AD_UNIT_KINDS.map((k) => (
          <Field key={k} label={UNIT_LABEL[k]}>
            <TextInput
              value={draft.units[k]}
              placeholder={view === "admanager" ? "/<network>/<unit>" : "ca-app-pub-…/…"}
              onChange={(e) => set({ unit: [k, e.target.value] })}
              readOnly={!canConfigure}
              aria-invalid={unitProblems[k] ? true : undefined}
              aria-label={`${UNIT_LABEL[k]} unit id`}
              spellCheck={false}
              className={a.mono}
              data-testid={`unit-${k}`}
            />
            {unitProblems[k] ? <span className={pages.fieldError}>{unitProblems[k]}</span> : null}
          </Field>
        ))}
      </div>
      <div className={a.foot}>
        {canConfigure ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setDraft(testIds)} disabled={same(draft, testIds)}>
              Use Google&rsquo;s test ids
            </Button>
            {dirty ? (
              <Button size="sm" variant="ghost" onClick={() => setDraft(server)}>
                Undo
              </Button>
            ) : null}
            {dirty ? (
              <Button
                size="sm"
                variant="primary"
                disabled={!valid}
                onClick={() =>
                  request(
                    key,
                    {
                      appId: draft.appId.trim(),
                      units: Object.fromEntries(AD_UNIT_KINDS.map((u) => [u, draft.units[u].trim()])),
                    },
                    `Save the ${PROVIDER_LABEL[view]} ids?`,
                    view === active && s.testMode ? "Test mode is on, so the apps keep loading test units until it is turned off." : undefined,
                  )
                }
                data-testid="save-ids"
              >
                Save ids
              </Button>
            ) : null}
          </>
        ) : (
          <ReadOnly>Read-only · unit ids need the admin role</ReadOnly>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- formats

function FormatsCard({ s, canEdit, request }: { s: AdsSettings; canEdit: boolean; request: RequestFn }) {
  const [draft, setDraft, dirty] = useDraft<AdFormats>(s.formats);
  const keys = Object.keys(FORMAT_LABEL) as (keyof AdFormats)[];
  return (
    <Card title="Formats">
      <div className={a.toggles}>
        {keys.map((k) => (
          <label key={k} className={a.toggle}>
            <Switch
              checked={draft[k]}
              label={`${FORMAT_LABEL[k]} (ads.formats.${k})`}
              disabled={!canEdit}
              testId={`format-${k}`}
              onClick={() => setDraft({ ...draft, [k]: !draft[k] })}
            />
            <span>
              <strong>{FORMAT_LABEL[k]}</strong>
              <span className={a.toggleNote}>
                {k === "rewarded"
                  ? "Optional, the player chooses"
                  : k === "interstitial"
                    ? "Between levels only"
                    : k === "banner"
                      ? "Levels map and Stats"
                      : "Back from background"}
              </span>
            </span>
          </label>
        ))}
      </div>
      <div className={a.foot}>
        {canEdit ? (
          <>
            {dirty ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => setDraft(s.formats)}>
                  Undo
                </Button>
                <Button size="sm" variant="primary" onClick={() => request("ads.formats", draft, "Save ad formats?")} data-testid="save-formats">
                  Save formats
                </Button>
              </>
            ) : (
              <span className="monoLabel">ads.formats · editor</span>
            )}
          </>
        ) : (
          <ReadOnly>Read-only · formats need the editor role</ReadOnly>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- placements

function PlacementsCard({ s, canEdit, request }: { s: AdsSettings; canEdit: boolean; request: RequestFn }) {
  const [draft, setDraft, dirty] = useDraft<AdPlacements>(s.placements);
  const [text, setText] = useState<Record<string, string>>({});
  const problems: string[] = [];
  for (const p of PLACEMENTS) {
    for (const f of p.fields) {
      const raw = text[`${p.key}.${f}`];
      if (raw === undefined) continue;
      const { min, max } = FIELD_LABEL[f];
      const n = Number(raw);
      if (raw.trim() === "" || !Number.isInteger(n) || n < min || n > max)
        problems.push(`${p.label}: ${FIELD_LABEL[f].label} must be a whole number from ${min} to ${max}.`);
    }
  }
  const setField = (key: keyof AdPlacements, f: PlacementField, raw: string) => {
    setText({ ...text, [`${key}.${f}`]: raw });
    const n = Number(raw);
    const { min, max } = FIELD_LABEL[f];
    if (raw.trim() !== "" && Number.isInteger(n) && n >= min && n <= max) setDraft({ ...draft, [key]: { ...draft[key], [f]: n } } as AdPlacements);
  };
  const undo = () => {
    setDraft(s.placements);
    setText({});
  };
  return (
    <Card
      title="Placements"
      className={a.wide}
      actions={
        canEdit ? (
          <>
            {dirty || Object.keys(text).length ? (
              <>
                <Button size="sm" variant="ghost" onClick={undo}>
                  Undo
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!dirty || problems.length > 0}
                  onClick={() => request("ads.placements", draft, "Save placements?")}
                  data-testid="save-placements"
                >
                  Save placements
                </Button>
              </>
            ) : (
              <span className="monoLabel">ads.placements · editor</span>
            )}
          </>
        ) : (
          <ReadOnly>Read-only · placements need the editor role</ReadOnly>
        )
      }
    >
      <div className={tableStyles.wrap}>
        <table className={tableStyles.table} data-testid="placements">
          <thead>
            <tr>
              <th style={{ width: "34%" }}>Placement</th>
              <th style={{ width: 90 }}>On</th>
              <th>Reward, caps and intervals</th>
            </tr>
          </thead>
          <tbody>
            {PLACEMENTS.map((p) => {
              const value = draft[p.key] as Record<string, number | boolean>;
              const formatOff = !s.formats[p.format];
              return (
                <tr key={p.key} data-placement={p.key}>
                  <td>
                    <div className={a.placementName}>
                      <strong>{p.label}</strong>
                      <Chip tone={p.format === "rewarded" ? "violet" : "neutral"}>{FORMAT_LABEL[p.format].toLowerCase()}</Chip>
                      {formatOff ? <Chip tone="muted">format off</Chip> : null}
                    </div>
                    <span className={pages.configDesc}>{p.note}</span>
                  </td>
                  <td>
                    <Switch
                      checked={Boolean(value.enabled)}
                      label={`${p.label} on`}
                      disabled={!canEdit}
                      testId={`placement-${p.key}`}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          [p.key]: { ...draft[p.key], enabled: !value.enabled },
                        } as AdPlacements)
                      }
                    />
                  </td>
                  <td>
                    {p.fields.length ? (
                      <div className={a.numbers}>
                        {p.fields.map((f) => {
                          const k = `${p.key}.${f}`;
                          return (
                            <label key={f} className={a.number}>
                              <span className="monoLabel">{FIELD_LABEL[f].label}</span>
                              <TextInput
                                inputMode="numeric"
                                value={text[k] ?? String(value[f] ?? "")}
                                onChange={(e) => setField(p.key, f, e.target.value)}
                                readOnly={!canEdit}
                                aria-label={`${p.label} ${FIELD_LABEL[f].label}`}
                                aria-invalid={problems.some((m) => m.startsWith(`${p.label}: ${FIELD_LABEL[f].label}`)) || undefined}
                                className={a.numberInput}
                              />
                              <span className={a.unit}>{f === "reward" ? "levels" : FIELD_LABEL[f].unit}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <span className={pages.faint}>on or off</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {problems.length ? (
        <div className={a.problems} role="alert">
          {problems.map((m) => (
            <span key={m} className={pages.fieldError}>
              {m}
            </span>
          ))}
        </div>
      ) : null}
      <p className={a.cardNote}>
        Rewarded caps are enforced by the server (daily per UTC day, the streak freeze per ISO week). Interstitial, banner and app-open rules run in
        the app, which also holds them back during the new-player grace.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------- the web (contracts/web.md §4.6)

const CMP_OPTIONS: { value: WebCmp; label: string }[] = [
  { value: "google", label: "Google consent message" },
  { value: "none", label: "None · non-personalized" },
];

function WebCard({ s, canConfigure, request }: { s: AdsSettings; canConfigure: boolean; request: RequestFn }) {
  const [draft, setDraft, dirty] = useDraft<AdsWeb>(s.web);
  const codeProblem = networkCodeProblem(draft);
  const unitProblems = Object.fromEntries(WEB_AD_UNITS.map((u) => [u, webUnitProblem(draft.networkCode, draft.units[u])])) as Record<WebAdUnit, string | null>;
  const valid = !codeProblem && WEB_AD_UNITS.every((u) => unitProblems[u] === null);
  const noUnits = WEB_AD_UNITS.every((u) => !draft.units[u].trim());
  const cleaned: AdsWeb = {
    enabled: draft.enabled,
    networkCode: draft.networkCode.trim(),
    units: { rewarded: draft.units.rewarded.trim(), banner: draft.units.banner.trim(), interstitial: draft.units.interstitial.trim() },
    cmp: draft.cmp,
  };
  return (
    <Card
      title="Web"
      className={a.wide}
      actions={<Chip tone={s.web.enabled ? "accent" : "muted"}>{s.web.enabled ? "web ads on" : "web ads off"}</Chip>}
    >
      <div data-testid="ads-web">
        <div className={a.switchRow}>
          <Switch
            checked={draft.enabled}
            label="Ads on the web (ads.web.enabled)"
            disabled={!canConfigure}
            testId="ads-web-enabled"
            onClick={() => setDraft({ ...draft, enabled: !draft.enabled })}
          />
          <div className={a.switchText}>
            <strong>{draft.enabled ? "Ads on iqdaily.app/play for free players" : "No ads on the web"}</strong>
            <p>
              Google Publisher Tags with Ad Manager web units, the same caps and placements as the app. Never on focus-mode or competitive screens. With no
              unit set, the web shows no ads and no reward offers.
            </p>
          </div>
        </div>
        <div className={a.fields}>
          <Field label="Network code" hint="Ad Manager → Admin → Global settings.">
            <TextInput
              value={draft.networkCode}
              inputMode="numeric"
              placeholder="21775744923"
              onChange={(e) => setDraft({ ...draft, networkCode: e.target.value })}
              readOnly={!canConfigure}
              aria-invalid={codeProblem ? true : undefined}
              aria-label="Ad Manager network code"
              spellCheck={false}
              className={a.mono}
              data-testid="ads-web-network"
            />
            {codeProblem ? <span className={pages.fieldError}>{codeProblem}</span> : null}
          </Field>
          {WEB_AD_UNITS.map((u) => (
            <Field key={u} label={`${WEB_UNIT_LABEL[u]} unit`}>
              <TextInput
                value={draft.units[u]}
                placeholder={`/${draft.networkCode.trim() || "<network>"}/iqdaily-web/${u}`}
                onChange={(e) => setDraft({ ...draft, units: { ...draft.units, [u]: e.target.value } })}
                readOnly={!canConfigure}
                aria-invalid={unitProblems[u] ? true : undefined}
                aria-label={`Web ${WEB_UNIT_LABEL[u].toLowerCase()} unit path`}
                spellCheck={false}
                className={a.mono}
                data-testid={`ads-web-unit-${u}`}
              />
              {unitProblems[u] ? <span className={pages.fieldError}>{unitProblems[u]}</span> : null}
            </Field>
          ))}
        </div>
        <div className={a.rule}>
          <span className="monoLabel">Consent</span>
          <Segmented label="Web consent" value={draft.cmp} options={CMP_OPTIONS} disabled={!canConfigure} onChange={(v) => setDraft({ ...draft, cmp: v })} />
          <span className={a.toggleNote} data-testid="ads-web-consent-note">
            {draft.cmp === "google"
              ? "The web shows Google's own consent message, which loads with the ads. Create and publish it first in Ad Manager → Privacy & messaging; the web doesn't ask for consent itself."
              : "No consent message: every ad is requested non-personalized."}{" "}
            There is no ATT prompt on the web.
          </span>
        </div>
        {draft.enabled && noUnits ? (
          <p className={a.cardNote} data-testid="ads-web-no-units">
            Web ads are on but no unit is set, so the web shows no ads yet.
          </p>
        ) : null}
        <div className={a.foot}>
          {canConfigure ? (
            dirty ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => setDraft(s.web)}>
                  Undo
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!valid}
                  onClick={() =>
                    request(
                      "ads.web",
                      cleaned,
                      cleaned.enabled !== s.web.enabled ? (cleaned.enabled ? "Turn on ads on the web?" : "Turn off ads on the web?") : "Save web ads?",
                      cleaned.enabled && cleaned.cmp === "google" ? "Check that the consent message is published in Ad Manager → Privacy & messaging." : undefined,
                    )
                  }
                  data-testid="save-ads-web"
                >
                  Save web ads
                </Button>
              </>
            ) : (
              <span className="monoLabel">ads.web · admin</span>
            )
          ) : (
            <ReadOnly>Read-only · web ads need the admin role</ReadOnly>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- rules

function RulesCard({ s, canConfigure, request }: { s: AdsSettings; canConfigure: boolean; request: RequestFn }) {
  const [grace, setGrace] = useState<string | null>(null);
  const graceValue = grace ?? String(s.graceDays);
  const graceN = Number(graceValue);
  const graceOk = graceValue.trim() !== "" && Number.isInteger(graceN) && graceN >= 0 && graceN <= 60;
  const [countries, setCountries, countriesDirty] = useDraft<AdCountries>(s.countries);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const addCode = () => {
    const c = code.trim().toUpperCase();
    if (!COUNTRY.test(c)) return setCodeError("Use a two-letter ISO country code, e.g. DE.");
    if (countries.list.includes(c)) return setCodeError(`${c} is already on the list.`);
    setCountries({ ...countries, list: [...countries.list, c].sort() });
    setCode("");
    setCodeError(null);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SSV_CALLBACK_URL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  const modeLabel = {
    all: "Everywhere",
    allow: "Only these",
    deny: "All but these",
  } as const;

  return (
    <Card title="Rules">
      <div className={a.rule}>
        <Field label="New-player grace" hint="Days after install with no interstitial, banner or app-open ads. Rewarded offers stay.">
          <div className={a.inline}>
            <TextInput
              inputMode="numeric"
              value={graceValue}
              onChange={(e) => setGrace(e.target.value)}
              readOnly={!canConfigure}
              aria-invalid={!graceOk || undefined}
              className={a.numberInput}
              data-testid="grace-days"
            />
            <span className={a.unit}>days</span>
            {canConfigure && graceOk && graceN !== s.graceDays ? (
              <Button size="sm" variant="primary" onClick={() => request("ads.newUserGraceDays", graceN, "Change the new-player grace?")}>
                Save
              </Button>
            ) : null}
          </div>
          {!graceOk ? <span className={pages.fieldError}>A whole number from 0 to 60.</span> : null}
        </Field>
      </div>

      <div className={a.rule} data-testid="countries">
        <span className="monoLabel">Countries</span>
        <Segmented
          label="Country filter"
          value={countries.mode}
          options={(["all", "allow", "deny"] as const).map((m) => ({
            value: m,
            label: modeLabel[m],
          }))}
          disabled={!canConfigure}
          onChange={(m) => setCountries({ ...countries, mode: m })}
        />
        {countries.mode !== "all" ? (
          <>
            <div className={a.codes} role="list" aria-label="Countries">
              {countries.list.length ? (
                countries.list.map((c) => (
                  <span key={c} role="listitem" className={a.code}>
                    <span className="mono">{c}</span>
                    {canConfigure ? (
                      <button
                        type="button"
                        aria-label={`Remove ${c}`}
                        onClick={() =>
                          setCountries({
                            ...countries,
                            list: countries.list.filter((x) => x !== c),
                          })
                        }
                      >
                        ×
                      </button>
                    ) : null}
                  </span>
                ))
              ) : (
                <span className={pages.faint}>{countries.mode === "allow" ? "No countries: nobody sees ads." : "No countries: everywhere."}</span>
              )}
            </div>
            {canConfigure ? (
              <form
                className={a.inline}
                onSubmit={(e) => {
                  e.preventDefault();
                  addCode();
                }}
              >
                <TextInput
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  maxLength={2}
                  placeholder="DE"
                  aria-label="Add a country code"
                  className={a.codeInput}
                />
                <Button size="sm" type="submit" variant="secondary" disabled={!code}>
                  Add
                </Button>
                {codeError ? <span className={pages.fieldError}>{codeError}</span> : null}
              </form>
            ) : null}
          </>
        ) : null}
        <span className={a.toggleNote}>
          From the request&rsquo;s <code>CF-IPCountry</code>, else the device region. An unknown country counts as allowed except on an allow list.
        </span>
        {canConfigure && countriesDirty ? (
          <div className={a.inline}>
            <Button size="sm" variant="ghost" onClick={() => setCountries(s.countries)}>
              Undo
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => request("ads.countries", countries, "Change the country filter?")}
              data-testid="save-countries"
            >
              Save countries
            </Button>
          </div>
        ) : null}
      </div>

      {s.provider === "admob" ? (
        <div className={a.rule} data-testid="ssv">
          <div className={a.switchRow}>
            <Switch
              checked={s.requireSsv}
              label="Require SSV (ads.rewards.requireSsv)"
              disabled={!canConfigure}
              testId="ads-ssv"
              onClick={() =>
                request(
                  "ads.rewards.requireSsv",
                  !s.requireSsv,
                  s.requireSsv ? "Stop waiting for SSV?" : "Require SSV for AdMob rewards?",
                  s.requireSsv
                    ? undefined
                    : "Only once every rewarded unit in AdMob has the callback URL below. Test units never call it, so test mode skips it.",
                )
              }
            />
            <div className={a.switchText}>
              <strong>{s.requireSsv ? "Rewards wait for Google's SSV callback" : "Rewards don't wait for SSV"}</strong>
              <p>Server-side verification: Google signs a callback per reward; claims answer 202 until it arrives.</p>
            </div>
          </div>
          <div className={a.copyRow}>
            <code data-testid="ssv-url">{SSV_CALLBACK_URL}</code>
            <Button size="sm" variant="secondary" onClick={() => void copy()} data-testid="copy-ssv">
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <span className={a.toggleNote}>Paste it into each rewarded ad unit in AdMob (Server-side verification → Callback URL).</span>
        </div>
      ) : (
        <p className={a.cardNote} data-testid="ssv-note">
          {s.provider === "admanager"
            ? "Ad Manager has no server-side verification: the app claims a reward when the SDK reports it; the server grants it at least 10 s after the intent, once, within the caps and rate limits, and counts unusual patterns in the stats."
            : "Unity is reserved: the apps ignore it, so ads are off with this provider."}
        </p>
      )}
      {!canConfigure ? <ReadOnly>Read-only · rules need the admin role</ReadOnly> : null}
    </Card>
  );
}

// ---------------------------------------------------------------- stats

const RANGES = [
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
] as const;

function StatsSection() {
  const [range, setRange] = useState<"7" | "14" | "30">("14");
  const to = todayUtc();
  const from = addDays(to, -(Number(range) - 1));
  const q = useAdsStats(from, to);
  return (
    <section className={a.stats} aria-labelledby="ads-stats-title">
      <div className={a.statsHead}>
        <h2 id="ads-stats-title" className={a.statsTitle}>
          Rewarded ads
        </h2>
        <span className={pages.faint}>
          {formatShortDate(from)} – {formatShortDate(to)} (UTC)
          {q.data && !q.data.fill ? " · fill isn’t reported by the apps yet" : ""}
        </span>
        <Segmented label="Stats range" value={range} options={[...RANGES]} onChange={setRange} />
      </div>
      {q.isPending ? (
        <Loading rows={6} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <StatsView data={q.data} fetching={q.isFetching} />
      )}
    </section>
  );
}

function StatsView({ data, fetching }: { data: AdsStats; fetching: boolean }) {
  const [placement, setPlacement] = useState<"all" | RewardedPlacement>("all");
  const t = data.totals;
  const columns = data.days.map((d) => ({
    key: d.date,
    label: formatShortDate(d.date),
    value: placement === "all" ? d.granted : d.grants[placement],
    notes:
      placement === "all"
        ? [`${fmtInt(d.intents)} intents`, ...(d.ssvVerified ? [`${fmtInt(d.ssvVerified)} SSV verified`] : [])]
        : [REWARDED_LABEL[placement]],
  }));
  const byPlacement: BarDatum[] = REWARDED_PLACEMENTS.map((p) => ({
    key: p,
    label: REWARDED_LABEL[p],
    value: t.grants[p],
    note:
      p === "rewardedExtraLevels"
        ? `${fmtInt(t.bonusLevels)} bonus levels`
        : p === "rewardedStreakFreeze"
          ? `${fmtInt(t.freezesDropped)} dropped (bank full)`
          : undefined,
  }));
  const ab = data.abuse;
  const signals: { label: string; value: number; note: string }[] = [
    {
      label: "Early claims",
      value: ab.earlyClaims,
      note: "claims before the 10 s floor (answered 202)",
    },
    {
      label: "Near the floor",
      value: ab.nearFloor,
      note: "grants claimed 10–15 s after the intent",
    },
    {
      label: "Bursts",
      value: ab.burst,
      note: "grants with 4+ others by the player in 10 minutes",
    },
    {
      label: "Unit mismatch",
      value: ab.unitMismatch,
      note: "claimed ad unit ≠ the one handed out",
    },
    {
      label: "Repeat claims",
      value: ab.repeatClaims,
      note: "claims of an intent already granted",
    },
    {
      label: "Rate-limited",
      value: ab.rateLimited,
      note: "intent or claim refused by the per-player or per-IP limit",
    },
    {
      label: "Shared IPs",
      value: ab.sharedIps,
      note: "IPs with grants for 5+ players",
    },
  ];
  const verifiedShare = t.granted ? data.ssv.verified / t.granted : null;
  return (
    <div aria-busy={fetching} data-testid="ads-stats">
      <div className={pages.statStrip}>
        <Stat label="Intents" value={fmtInt(t.intents)} sub={`${fmtInt(t.open)} open now`} />
        <Stat label="Grants" value={fmtInt(t.granted)} sub={t.intents ? `${fmtPct(t.granted / t.intents)} of intents` : "no intents"} />
        <Stat label="Bonus levels" value={fmtInt(t.bonusLevels)} sub="extra free levels" />
        <Stat label="Freezes" value={fmtInt(t.freezes)} sub={`${fmtInt(t.freezesDropped)} dropped`} />
        <Stat label="Archive unlocks" value={fmtInt(t.archiveUnlocks)} sub="24 h each" />
        <Stat label="SSV verified" value={fmtPct(verifiedShare)} sub={`${fmtInt(data.ssv.verified)} of ${fmtInt(t.granted)} grants`} />
        {data.fill ? (
          <Stat label="Fill" value={fmtPct(data.fill.ratio)} sub={`${fmtInt(data.fill.filled)} of ${fmtInt(data.fill.requests)} requests`} />
        ) : null}
      </div>
      <div className={pages.moneyGrid}>
        <Card
          title="Grants per day"
          className={pages.moneyWide}
          actions={
            <Segmented
              label="Placement"
              value={placement}
              options={[
                { value: "all", label: "All" },
                ...REWARDED_PLACEMENTS.map((p) => ({
                  value: p,
                  label: REWARDED_LABEL[p],
                })),
              ]}
              onChange={setPlacement}
            />
          }
        >
          {t.granted ? (
            <ColumnChart
              rows={columns}
              label={`Rewarded grants per UTC day${placement === "all" ? "" : `: ${REWARDED_LABEL[placement]}`}`}
              valueName="grants"
            />
          ) : (
            <EmptyState title="No rewards yet">Grants show here once free players watch rewarded ads.</EmptyState>
          )}
        </Card>
        <Card title="Grants by placement" actions={<span className="mono">{fmtInt(t.granted)} total</span>}>
          {t.granted ? (
            <BarChart rows={byPlacement} label="Rewarded grants by placement" valueName="grants" labelWidth={116} />
          ) : (
            <EmptyState title="No rewards yet" />
          )}
        </Card>
        <Card title="Anti-abuse signals" actions={<span className="monoLabel">Ad Manager rewards are client-reported</span>}>
          <ul className={a.signals} data-testid="abuse">
            {signals.map((sig) => (
              <li key={sig.label}>
                <span
                  className={a.signalValue}
                  data-flag={sig.value > 0 && sig.label !== "Early claims" && sig.label !== "Repeat claims" ? true : undefined}
                >
                  {fmtInt(sig.value)}
                </span>
                <span>
                  <strong>{sig.label}</strong>
                  <span className={a.toggleNote}>{sig.note}</span>
                </span>
              </li>
            ))}
          </ul>
          {ab.topPlayers.length ? (
            <table className={tableStyles.table} data-testid="top-players">
              <thead>
                <tr>
                  <th>Most rewards</th>
                  <th data-align="right">Grants</th>
                  <th data-align="right">Flagged</th>
                </tr>
              </thead>
              <tbody>
                {ab.topPlayers.map((p) => (
                  <tr key={p.userId}>
                    <td>
                      <Link to={`/players/${p.userId}`} className={tableStyles.id}>
                        {shortId(p.userId)}
                      </Link>
                    </td>
                    <td data-align="right">{fmtInt(p.grants)}</td>
                    <td data-align="right">{p.flagged ? <Chip tone="warning">{fmtInt(p.flagged)}</Chip> : "0"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
